import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { saveAs } from 'file-saver'

import anchor from '../../../constants/anchor'
import { DOCHUB_API_URL } from '../../api/apiUrls'

import {
    GetAnalyticsDealsBySellerApiArg,
    GetAnalyticsDealsBySellerApiResponse,
    GetAnalyticsDealsBySourceApiArg,
    GetAnalyticsDealsBySourceApiResponse,
    GetAnalyticsDealsApiArg,
    GetAnalyticsDealsApiResponse,
    GetClientsListApiArg,
    GetClientsListApiResponse,
    GetClientsHistoryListApiArg,
    GetClientsHistoryListApiResponse,
    DeleteClientApiArg,
    DeleteClientApiResponse,
    GetDealsListApiArg,
    GetDealsListApiResponse,
    ExportDealsListApiArg,
    GetDealStagesInfoApiArg,
    GetDealStagesInfoApiResponse,
    GetDealStatusDictApiResponse,
    GetVendorDataApiArg,
    GetVendorDataApiResponse,
    SaveClientApiArg,
    SaveClientApiResponse,
    SaveDealApiArg,
    SaveDealApiResponse,
    UpdateVendorDataApiArg,
    UpdateVendorDataApiResponse,
} from './dochubTypes'

export const BASE_URL = DOCHUB_API_URL

const customBaseQuery = async (args: any, api: any, extraOptions: any) => {
    const baseQuery = fetchBaseQuery({ baseUrl: BASE_URL })
    const userId = localStorage.getItem('userId')
    const accessToken = localStorage.getItem('accessToken')
    const authorizationHeader = `${anchor} ${accessToken}`

    // Добавляем headers с Authorization и userId к каждому запросу
    if (typeof args === 'object') {
        args.headers = {
            ...args.headers,
            Authorization: authorizationHeader,
        }

        if (args.body) {
            args.body = { ...args.body, userId }
        }
    }

    return baseQuery(args, api, extraOptions)
}

export const dochubApi = createApi({
    reducerPath: 'dochubApi',
    baseQuery: customBaseQuery,
    endpoints: builder => ({
        getDealsList: builder.mutation<GetDealsListApiResponse, Omit<GetDealsListApiArg, 'userId'>>({
            query: queryArg => ({
                url: `/getDealsList`,
                method: 'POST',
                body: queryArg,
            }),
        }),
        exportDealsList: builder.mutation<Blob, Omit<ExportDealsListApiArg, 'userId'>>({
            query: queryArg => ({
                url: `/exportDealsList`,
                method: 'POST',
                body: queryArg,
                cache: 'no-cache',
                responseHandler: async (response: Response) => {
                    const blob = await response.blob()

                    let fileName = 'deals_export.xlsx'
                    const contentDisposition = response.headers.get('content-disposition')
                    if (contentDisposition) {
                        const match = contentDisposition.match(/filename="(.+?)"/)
                        if (match) fileName = match[1]
                    }

                    saveAs(blob, fileName)
                },
            }),
        }),
        getAnalyticsDeals: builder.mutation<GetAnalyticsDealsApiResponse, Omit<GetAnalyticsDealsApiArg, 'userId'>>({
            query: queryArg => ({
                url: `/getAnalyticsDeals`,
                method: 'POST',
                body: queryArg,
            }),
        }),
        getAnalyticsDealsBySeller: builder.mutation<
            GetAnalyticsDealsBySellerApiResponse,
            Omit<GetAnalyticsDealsBySellerApiArg, 'userId'>
        >({
            query: queryArg => ({
                url: `/getAnalyticsDealsBySeller`,
                method: 'POST',
                body: queryArg,
            }),
        }),
        getAnalyticsDealsBySource: builder.mutation<
            GetAnalyticsDealsBySourceApiResponse,
            Omit<GetAnalyticsDealsBySourceApiArg, 'userId'>
        >({
            query: queryArg => ({
                url: `/getAnalyticsDealsBySource`,
                method: 'POST',
                body: queryArg,
            }),
        }),
        saveDeal: builder.mutation<SaveDealApiResponse, Omit<SaveDealApiArg, 'userId'>>({
            query: queryArg => ({
                url: `/saveDeal`,
                method: 'POST',
                body: queryArg,
            }),
        }),
        getDealStagesInfo: builder.query<GetDealStagesInfoApiResponse, Omit<GetDealStagesInfoApiArg, 'userId'>>({
            query: queryArg => ({
                url: `/getDealStagesInfo`,
                method: 'POST',
                body: queryArg,
            }),
        }),
        getDealStatusDict: builder.query<GetDealStatusDictApiResponse, void>({
            query: queryArg => ({
                url: `/getDealStatusDict`,
                method: 'POST',
                body: queryArg,
            }),
        }),
        saveClient: builder.mutation<SaveClientApiResponse, Omit<SaveClientApiArg, 'userId'>>({
            query: queryArg => ({
                url: `/saveClient`,
                method: 'POST',
                body: queryArg,
            }),
        }),
        getClientsList: builder.query<GetClientsListApiResponse, Omit<GetClientsListApiArg, 'userId'>>({
            query: queryArg => ({
                url: `/getClientsList`,
                method: 'POST',
                body: queryArg,
            }),
        }),
        deleteClient: builder.mutation<DeleteClientApiResponse, Omit<DeleteClientApiArg, 'userId'>>({
            query: queryArg => ({
                url: `/deleteClient`,
                method: 'POST',
                body: queryArg,
            }),
        }),
        getClientsHistoryList: builder.query<
            GetClientsHistoryListApiResponse,
            Omit<GetClientsHistoryListApiArg, 'userId'>
        >({
            query: queryArg => ({
                url: `/getClientsHistoryList`,
                method: 'POST',
                body: queryArg,
            }),
        }),
        getVendorData: builder.query<GetVendorDataApiResponse, GetVendorDataApiArg>({
            query: queryArg => ({
                url: `/getVendorData`,
                method: 'POST',
                body: queryArg,
            }),
        }),
        updateVendorData: builder.mutation<UpdateVendorDataApiResponse, UpdateVendorDataApiArg>({
            query: queryArg => ({
                url: `/updateVendorData`,
                method: 'POST',
                body: queryArg,
            }),
        }),
        downloadDealDocument: builder.mutation<Blob, { dealId: string }>({
            query: queryArg => ({
                url: `/downloadDealDocument`,
                method: 'POST',
                body: queryArg,
                cache: 'no-cache',
                responseHandler: async (response: Response) => {
                    const blob = await response.blob()

                    let fileName = `${queryArg.dealId}.pdf`
                    const contentDisposition = response.headers.get('content-disposition')
                    if (contentDisposition) {
                        const match = contentDisposition.match(/filename="(.+?)"/)
                        if (match) fileName = match[1]
                    }

                    saveAs(blob, fileName)
                },
            }),
        }),
    }),
})

export const {
    useGetDealsListMutation,
    useExportDealsListMutation,
    useGetAnalyticsDealsMutation,
    useGetAnalyticsDealsBySellerMutation,
    useGetAnalyticsDealsBySourceMutation,
    useSaveDealMutation,
    useGetDealStagesInfoQuery,
    useGetDealStatusDictQuery,
    useSaveClientMutation,
    useGetClientsListQuery,
    useLazyGetClientsListQuery,
    useDeleteClientMutation,
    useGetClientsHistoryListQuery,
    useGetVendorDataQuery,
    useUpdateVendorDataMutation,
    useDownloadDealDocumentMutation,
} = dochubApi
